import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  Settings, 
  LayoutDashboard,
  ChevronLeft,
  ChevronRight,
  Building2,
  Home,
  HelpCircle,
  CalendarDays,
  BarChart3,
  UserPlus,
  MapPin,
  DollarSign,
  Link as LinkIcon,
  UsersIcon,
  BookOpen,
  ChevronDown,
  Clipboard,
  Bell,
  Zap,
  Palette,
  Activity,
  FileText,
  Brain
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/contexts/PermissionContext";
import { AdminGuard, ConditionalRender } from "@/components/PermissionGuard";
import { useOnboardingAccess } from "@/hooks/useOnboardingAccess";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabaseClient";
import { TenantDropdown } from "@/components/layout/TenantDropdown";
import { filterPipelinesByAccess, hasGeneralPipelinePermission } from "@/utils/pipelinePermissions";

type NavItem = {
  title: string;
  href: string;
  icon: React.ReactNode;
  isVisible?: boolean;
};

type Pipeline = {
  id: number;
  name: string;
  pipeline_type: 'commercial' | 'residential' | 'onboarding';
  role_permissions?: string[];
  user_permissions?: string[];
  team_permissions?: string[];
};

// Admin-only navigation items
const adminNavItems: NavItem[] = [
  { title: "Reporting", href: "/app/reporting", icon: <BarChart3 className="h-5 w-5" /> },
  { title: "Contracts", href: "/app/contracts", icon: <FileText className="h-5 w-5" /> },
  { title: "Tracking", href: "/app/tracking", icon: <MapPin className="h-5 w-5" /> }
];

const settingsSubmenuItems: NavItem[] = [
  { title: "Users", href: "/app/settings?tab=users", icon: <UsersIcon className="h-5 w-5" /> },
  { title: "Pipelines", href: "/app/settings?tab=pipelines", icon: <BarChart3 className="h-5 w-5" /> },
  { title: "Templates", href: "/app/settings?tab=templates", icon: <Clipboard className="h-5 w-5" /> },
  { title: "Notifications", href: "/app/settings?tab=notifications", icon: <Bell className="h-5 w-5" /> },
  { title: "Automation", href: "/app/settings?tab=webhooks", icon: <Zap className="h-5 w-5" /> },
  { title: "Theme", href: "/app/settings?tab=theme", icon: <Palette className="h-5 w-5" /> },
  { title: "Logs", href: "/app/settings?tab=logs", icon: <Activity className="h-5 w-5" /> },
  { title: "AI", href: "/app/settings?tab=knowledge", icon: <Brain className="h-5 w-5" /> },
];

type SidebarProps = {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onOpenAddUser?: () => void;
};

export function Sidebar({ collapsed = false, onToggleCollapse, onOpenAddUser }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();

  // No need for manual cleanup - let CSS classes handle everything
  const { user } = useAuth();
  const { hasPermission, permissions, roleInfo } = usePermissions();
  const { hasAccess: hasOnboardingAccess } = useOnboardingAccess();
  const canAddUsers = hasPermission('manage_users') || hasPermission('user.manage_all');
  const [teamId, setTeamId] = useState<number | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [commercialPipelines, setCommercialPipelines] = useState<Pipeline[]>([]);
  const [residentialPipelines, setResidentialPipelines] = useState<Pipeline[]>([]);
  const [internalPipelines, setInternalPipelines] = useState<Pipeline[]>([]);
  const [isCommercialOpen, setIsCommercialOpen] = useState(false);
  const [isResidentialOpen, setIsResidentialOpen] = useState(false);
  const [isInternalOpen, setIsInternalOpen] = useState(false);

  // Fetch the user's team ID
  useEffect(() => {
    if (user) {
      const fetchTeamId = async () => {
        const { data, error } = await supabase
          .from('user_teams')
          .select('team_id')
          .eq('user_id', user.id)
          .limit(1);
        if (error) {
          // Don't log this error as it's expected when user doesn't have a team assigned
          setTeamId(null);
        } else if (!data || data.length === 0) {
          // User doesn't have a team assigned - this is normal
          setTeamId(null);
        } else {
          setTeamId(data[0].team_id);
        }
      };
      fetchTeamId();
    } else {
      setTeamId(null);
    }
  }, [user]);

  // Fetch pipelines for submenu navigation with permission filtering
  useEffect(() => {
    const fetchPipelines = async () => {
      if (!user) return;

      try {
        // Get current tenant ID
        const { data: tenantId, error: tenantError } = await supabase.rpc('get_current_user_tenant_id');
        if (tenantError) {
          console.error('Error getting tenant ID:', tenantError);
          return;
        }

        // Fetch pipelines with permission data
        const { data, error } = await supabase
          .from('pipelines')
          .select('id, name, pipeline_type, role_permissions, user_permissions, team_permissions')
          .eq('tenant_id', tenantId)
          .order('name');

        if (error) {
          console.error('Error fetching pipelines:', error);
          return;
        }

        // Get user role information
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select(`
            id,
            role_id,
            roles (
              id,
              name
            )
          `)
          .eq('id', user.id)
          .single();

        if (userError) {
          console.error('Error fetching user role:', userError);
          return;
        }

        // Extract role name
        const roleData = Array.isArray(userData.roles) ? userData.roles[0] : userData.roles;
        const userRole = roleData?.name || '';

        // Check for system override permissions
        const hasSystemOverride = hasPermission('system.override') ||
                                 hasPermission('tenant.manage') ||
                                 userRole === 'Superadmin';

        // Create user info object for permission checking
        const userInfo = {
          id: user.id,
          role: userRole,
          teamId: teamId?.toString()
        };

        // Filter pipelines by type and apply permission filtering
        const allCommercial = data?.filter(p => p.pipeline_type === 'commercial') || [];
        const allResidential = data?.filter(p => p.pipeline_type === 'residential') || [];
        const allInternal = data?.filter(p => p.pipeline_type === 'onboarding') || [];

        // Apply permission filtering - only show pipelines with general permissions OR specific access
        const filteredCommercial = allCommercial.filter(pipeline => {
          // If user has general commercial pipeline permission, apply access filtering
          if (hasGeneralPipelinePermission('commercial', permissions)) {
            return filterPipelinesByAccess([pipeline], userInfo, hasSystemOverride).length > 0;
          }
          return false;
        });

        const filteredResidential = allResidential.filter(pipeline => {
          if (hasGeneralPipelinePermission('residential', permissions)) {
            return filterPipelinesByAccess([pipeline], userInfo, hasSystemOverride).length > 0;
          }
          return false;
        });

        const filteredInternal = allInternal.filter(pipeline => {
          if (hasGeneralPipelinePermission('onboarding', permissions)) {
            return filterPipelinesByAccess([pipeline], userInfo, hasSystemOverride).length > 0;
          }
          return false;
        });

        setCommercialPipelines(filteredCommercial);
        setResidentialPipelines(filteredResidential);
        setInternalPipelines(filteredInternal);

      } catch (error) {
        console.error('Error fetching pipelines:', error);
      }
    };

    fetchPipelines();
  }, [user, teamId, permissions, hasPermission]);

  // Auto-expand relevant submenu based on current path
  useEffect(() => {
    if (location.pathname === "/app/pipeline") {
      setIsCommercialOpen(true);
    } else if (location.pathname === "/app/pipeline-resi") {
      setIsResidentialOpen(true);
    } else if (location.pathname === "/app/onboarding") {
      setIsInternalOpen(true);
    }
  }, [location.pathname]);

  // Check if user can add team users (Team Owner, Team Admin, or full admin)
  // Only show Add User for Team Owners and Team Admins (full admins use settings menu)
  const canAddTeamUsers = roleInfo?.name === 'Team Owner' || roleInfo?.name === 'Team Admin';

  // Main navigation items defined inside the component to access teamId
  const mainNavItems: NavItem[] = [
    { title: "Dashboard", href: "/app/dashboard", icon: <LayoutDashboard className="h-5 w-5" />, isVisible: false },
    { title: "Dashboards", href: "/app/dashboards", icon: <LayoutDashboard className="h-5 w-5" /> },
    ...(teamId === 163
      ? [{
          title: "Aurora",
          href: "/app/aurora", // Internal route for Aurora iframe
          icon: <img src="/tets.png" className="h-5 w-5 min-w-[20px] min-h-[20px] object-contain" alt="Aurora icon" />
        }]
      : []),
    // Temporarily hidden - knowledgebase is outdated
    // { title: "Knowledge Base", href: "/app/faq", icon: <BookOpen className="h-5 w-5" /> }
  ];

  const isActive = (path: string) => {
    // Handle settings tabs with query parameters
    if (path.includes('?tab=') && location.pathname === '/app/settings' && location.search.includes(path.split('?tab=')[1])) {
      return true;
    }
    
    // Handle pipeline URLs with query parameters
    if (path.includes('?pipelineId=')) {
      const basePath = path.split('?')[0];
      return location.pathname === basePath;
    }
    
    // Default path matching
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const toggleSettingsSubmenu = () => {
    if (!collapsed) {
      setIsSettingsOpen(!isSettingsOpen);
    }
  };

  const toggleCommercialSubmenu = () => {
    if (!collapsed) {
      setIsCommercialOpen(!isCommercialOpen);
    }
  };

  const toggleResidentialSubmenu = () => {
    if (!collapsed) {
      setIsResidentialOpen(!isResidentialOpen);
    }
  };

  const toggleInternalSubmenu = () => {
    if (!collapsed) {
      setIsInternalOpen(!isInternalOpen);
    }
  };



  return (
    <aside 
      className={cn(
        "h-full flex-shrink-0 bg-zinc-900 z-[200] transition-all duration-300",
        collapsed ? "w-14" : "w-56"
      )}
      style={{ backgroundColor: 'var(--sidebar-color, #000000)' }}
    >
      <div className="flex flex-col h-full relative">
        {/* Navigation */}
        <div className="p-4 flex-grow overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-500">
          <nav className="space-y-1 pb-16">
            {/* Tenant Dropdown - appears above Dashboard */}
            <TenantDropdown collapsed={collapsed} />
            
            {/* Render main navigation items */}
            {mainNavItems.map((item) => (
              item.isVisible === undefined || item.isVisible ? (
                item.title === "Dashboards" ? (
                  // Custom handler for Dashboards to navigate directly to default/last viewed
                  <button
                    key={item.href}
                    onClick={async () => {
                      try {
                        const lastViewedId = localStorage.getItem('joules_last_viewed_dashboard_id');

                        if (lastViewedId) {
                          // Navigate to last viewed dashboard
                          navigate(`/app/dashboards/${lastViewedId}`);
                        } else {
                          // Fetch dashboards to find default
                          const { data: dashboards } = await supabase
                            .from('dashboards')
                            .select('*')
                            .order('created_at', { ascending: false });

                          const defaultDashboard = dashboards?.find((d: any) => d.is_default);
                          if (defaultDashboard) {
                            navigate(`/app/dashboards/${defaultDashboard.id}`);
                          } else if (dashboards && dashboards.length > 0) {
                            // Navigate to first dashboard if no default
                            navigate(`/app/dashboards/${dashboards[0].id}`);
                          } else {
                            // No dashboards exist - error state (shouldn't happen)
                            console.warn('No dashboards found for user');
                            navigate('/app/dashboard');
                          }
                        }
                      } catch (error) {
                        console.error('Failed to navigate to dashboard:', error);
                        navigate('/app/dashboard');
                      }
                    }}
                    data-nav-link
                    className={cn(
                      "flex items-center rounded-md text-sm font-medium transition-all duration-200 ease-in-out group",
                      collapsed ? "justify-center p-2" : "px-3 py-2",
                      isActive(item.href)
                        ? "bg-primary/20 text-primary border-l-2 border-primary"
                        : "hover:bg-gray-600 hover:text-white hover:border-l-2 hover:border-primary hover:translate-x-0.5 hover:shadow-md"
                    )}
                    style={{
                      color: isActive(item.href) ? undefined : 'var(--sidebar-text-color, #ffffff)',
                      backgroundColor: isActive(item.href) ? undefined : 'transparent',
                    }}
                    title={collapsed ? item.title : undefined}
                  >
                    <span className={cn("flex-shrink-0", collapsed ? "" : "mr-3")}>{item.icon}</span>
                    {!collapsed && <span>{item.title.trim()}</span>}
                  </button>
                ) : (
                  <Link
                    key={item.href}
                    to={item.href}
                    data-nav-link
                    className={cn(
                      "flex items-center rounded-md text-sm font-medium transition-all duration-200 ease-in-out group",
                      collapsed ? "justify-center p-2" : "px-3 py-2",
                      isActive(item.href)
                        ? "bg-primary/20 text-primary border-l-2 border-primary"
                        : "hover:bg-gray-600 hover:text-white hover:border-l-2 hover:border-primary hover:translate-x-0.5 hover:shadow-md"
                    )}
                    style={{
                      color: isActive(item.href) ? undefined : 'var(--sidebar-text-color, #ffffff)',
                      backgroundColor: isActive(item.href) ? undefined : 'transparent',
                    }}
                    title={collapsed ? item.title : undefined}
                  >
                    <span className={cn("flex-shrink-0", collapsed ? "" : "mr-3")}>{item.icon}</span>
                    {!collapsed && <span>{item.title.trim()}</span>}
                  </Link>
                )
              ) : null
            ))}

            {/* Commercial Pipelines */}
            <div>
              <button
                onClick={toggleCommercialSubmenu}
                className={cn(
                  "flex items-center rounded-md text-sm font-medium transition-all duration-200 ease-in-out w-full group",
                  collapsed ? "justify-center p-2" : "px-3 py-2",
                  location.pathname === "/app/pipeline"
                    ? "bg-primary/20 text-primary border-l-2 border-primary"
                    : "hover:bg-gray-600 hover:text-white hover:border-l-2 hover:border-primary hover:translate-x-0.5 hover:shadow-md"
                )}
                style={{
                  color: location.pathname === "/app/pipeline" ? undefined : 'var(--sidebar-text-color, #ffffff)',
                  backgroundColor: location.pathname === "/app/pipeline" ? undefined : 'transparent',
                }}

                title={collapsed ? "Commercial" : undefined}
              >
                <span className={cn("flex-shrink-0", collapsed ? "" : "mr-3")}>
                  <Building2 className="h-5 w-5" />
                </span>
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left">Commercial</span>
                    <ChevronDown className={cn(
                      "h-4 w-4 transition-transform flex-shrink-0",
                      isCommercialOpen ? "rotate-180" : ""
                    )} />
                  </>
                )}
              </button>
              {!collapsed && isCommercialOpen && (
                <div className="ml-1 space-y-1 border-l border-border ml-5 pl-3">
                  {commercialPipelines.map((pipeline) => (
                    <Link
                      key={pipeline.id}
                      to={`/app/pipeline?pipelineId=${pipeline.id}`}
                      className={cn(
                        "flex items-center rounded-md text-xs font-medium transition-all duration-200 ease-in-out px-3 py-1.5 relative",
                        location.pathname === "/app/pipeline" && location.search.includes(`pipelineId=${pipeline.id}`)
                          ? "bg-primary/15 text-primary border-l-2 border-primary"
                          : "hover:bg-gray-600 hover:text-white hover:border-l-2 hover:border-primary hover:translate-x-0.5 hover:shadow-md"
                      )}
                      style={{
                        color: (location.pathname === "/app/pipeline" && location.search.includes(`pipelineId=${pipeline.id}`)) 
                          ? undefined 
                          : 'var(--sidebar-text-color, #ffffff)',
                        backgroundColor: (location.pathname === "/app/pipeline" && location.search.includes(`pipelineId=${pipeline.id}`)) 
                          ? undefined 
                          : 'transparent',
                      }}
                    >
                      <span className="mr-2 flex-shrink-0">
                        <Building2 className="h-3 w-3" />
                      </span>
                      <span className="truncate">{pipeline.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Residential Pipelines */}
            <div>
              <button
                onClick={toggleResidentialSubmenu}
                className={cn(
                  "flex items-center rounded-md text-sm font-medium transition-all duration-200 ease-in-out w-full group",
                  collapsed ? "justify-center p-2" : "px-3 py-2",
                  location.pathname === "/app/pipeline-resi"
                    ? "bg-primary/20 text-primary border-l-2 border-primary"
                    : "hover:bg-gray-600 hover:text-white hover:border-l-2 hover:border-primary hover:translate-x-0.5 hover:shadow-md"
                )}
                style={{
                  color: location.pathname === "/app/pipeline-resi" ? undefined : 'var(--sidebar-text-color, #ffffff)',
                  backgroundColor: location.pathname === "/app/pipeline-resi" ? undefined : 'transparent',
                }}
                title={collapsed ? "Residential" : undefined}
              >
                <span className={cn("flex-shrink-0", collapsed ? "" : "mr-3")}>
                  <Home className="h-5 w-5" />
                </span>
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left">Residential</span>
                    <ChevronDown className={cn(
                      "h-4 w-4 transition-transform flex-shrink-0",
                      isResidentialOpen ? "rotate-180" : ""
                    )} />
                  </>
                )}
              </button>
              {!collapsed && isResidentialOpen && (
                <div className="ml-1 space-y-1 border-l border-border ml-5 pl-3">
                  {residentialPipelines.map((pipeline) => (
                    <Link
                      key={pipeline.id}
                      to={`/app/pipeline-resi?pipelineId=${pipeline.id}`}
                      className={cn(
                        "flex items-center rounded-md text-xs font-medium transition-all duration-200 ease-in-out px-3 py-1.5 relative",
                        location.pathname === "/app/pipeline-resi" && location.search.includes(`pipelineId=${pipeline.id}`)
                          ? "bg-primary/15 text-primary border-l-2 border-primary"
                          : "hover:bg-gray-600 hover:text-white hover:border-l-2 hover:border-primary hover:translate-x-0.5 hover:shadow-md"
                      )}
                      style={{
                        color: (location.pathname === "/app/pipeline-resi" && location.search.includes(`pipelineId=${pipeline.id}`)) 
                          ? undefined 
                          : 'var(--sidebar-text-color, #ffffff)',
                        backgroundColor: (location.pathname === "/app/pipeline-resi" && location.search.includes(`pipelineId=${pipeline.id}`)) 
                          ? undefined 
                          : 'transparent',
                      }}
                    >
                      <span className="mr-2 flex-shrink-0">
                        <Home className="h-3 w-3" />
                      </span>
                      <span className="truncate">{pipeline.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Internal Pipelines */}
            {hasOnboardingAccess && (
              <div>
                <button
                  onClick={toggleInternalSubmenu}
                  className={cn(
                    "flex items-center rounded-md text-sm font-medium transition-all duration-200 ease-in-out w-full group",
                    collapsed ? "justify-center p-2" : "px-3 py-2",
                    location.pathname === "/app/onboarding"
                      ? "bg-primary/20 text-primary border-l-2 border-primary"
                      : "hover:bg-gray-600 hover:text-white hover:border-l-2 hover:border-primary hover:translate-x-0.5 hover:shadow-md"
                  )}
                  style={{
                    color: location.pathname === "/app/onboarding" ? undefined : 'var(--sidebar-text-color, #ffffff)',
                    backgroundColor: location.pathname === "/app/onboarding" ? undefined : 'transparent',
                  }}
                  title={collapsed ? "Internal" : undefined}
                >
                  <span className={cn("flex-shrink-0", collapsed ? "" : "mr-3")}>
                    <UsersIcon className="h-5 w-5" />
                  </span>
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-left">Internal</span>
                      <ChevronDown className={cn(
                        "h-4 w-4 transition-transform flex-shrink-0",
                        isInternalOpen ? "rotate-180" : ""
                      )} />
                    </>
                  )}
                </button>
                {!collapsed && isInternalOpen && (
                  <div className="ml-1 space-y-1 border-l border-border ml-5 pl-3">
                    {internalPipelines.map((pipeline) => (
                      <Link
                        key={pipeline.id}
                        to={`/app/onboarding?pipelineId=${pipeline.id}`}
                        className={cn(
                          "flex items-center rounded-md text-xs font-medium transition-all duration-200 ease-in-out px-3 py-1.5 relative",
                          location.pathname === "/app/onboarding" && location.search.includes(`pipelineId=${pipeline.id}`)
                            ? "bg-primary/15 text-primary border-l-2 border-primary"
                            : "hover:bg-gray-600 hover:text-white hover:border-l-2 hover:border-primary hover:translate-x-0.5 hover:shadow-md"
                        )}
                        style={{
                          color: (location.pathname === "/app/onboarding" && location.search.includes(`pipelineId=${pipeline.id}`)) 
                            ? undefined 
                            : 'var(--sidebar-text-color, #ffffff)',
                          backgroundColor: (location.pathname === "/app/onboarding" && location.search.includes(`pipelineId=${pipeline.id}`)) 
                            ? undefined 
                            : 'transparent',
                        }}
                      >
                        <span className="mr-2 flex-shrink-0">
                          <UsersIcon className="h-3 w-3" />
                        </span>
                        <span className="truncate">{pipeline.name}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Add User Link for Team Owners/Admins */}
            {canAddTeamUsers && (
              <button
                onClick={onOpenAddUser}
                className={cn(
                  "flex items-center rounded-md text-sm font-medium transition-all duration-200 ease-in-out group w-full",
                  collapsed ? "justify-center p-2" : "px-3 py-2",
                  "hover:bg-gray-600 hover:text-white hover:border-l-2 hover:border-primary hover:translate-x-0.5 hover:shadow-md"
                )}
                style={{
                  color: 'var(--sidebar-text-color, #ffffff)',
                  backgroundColor: 'transparent',
                }}
                title={collapsed ? "Add User" : undefined}
              >
                <span className={cn("flex-shrink-0", collapsed ? "" : "mr-3")}>
                  <UserPlus className="h-5 w-5" />
                </span>
                {!collapsed && <span>Add User</span>}
              </button>
            )}

            {/* Render admin navigation items */}
            <ConditionalRender permission="reporting.admin">
              {adminNavItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "flex items-center rounded-md text-sm font-medium transition-all duration-200 ease-in-out",
                    collapsed ? "justify-center p-2" : "px-3 py-2",
                    isActive(item.href)
                      ? "bg-primary/20 text-primary border-l-2 border-primary"
                      : "hover:bg-gray-600 hover:text-white hover:border-l-2 hover:border-primary hover:translate-x-0.5 hover:shadow-md"
                  )}
                  style={{
                    color: isActive(item.href) ? undefined : 'var(--sidebar-text-color, #ffffff)',
                    backgroundColor: isActive(item.href) ? undefined : 'transparent',
                  }}
                  title={collapsed ? item.title : undefined}
                >
                  <span className={cn("flex-shrink-0", collapsed ? "" : "mr-3")}>{item.icon}</span>
                  {!collapsed && <span>{item.title.trim()}</span>}
                </Link>
              ))}
            </ConditionalRender>

            {/* Add separator and spacing before Settings */}
            <ConditionalRender permissions={["manage_settings", "system.override"]} requireAll={false}>
              <div className={cn(
                "border-t-2 border-border my-2",
                collapsed ? "mx-2" : "mx-3"
              )} />
            </ConditionalRender>

            {/* Render Settings link */}
            <ConditionalRender permissions={["manage_settings", "system.override"]} requireAll={false}>
              <div>
                <button
                  onClick={toggleSettingsSubmenu}
                  className={cn(
                    "flex items-center rounded-md text-sm font-medium transition-all duration-200 ease-in-out w-full group",
                    collapsed ? "justify-center p-2" : "px-3 py-2",
                    isActive("/app/settings")
                      ? "bg-primary/20 text-primary border-l-2 border-primary"
                      : "hover:bg-gray-600 hover:text-white hover:border-l-2 hover:border-primary hover:translate-x-0.5 hover:shadow-md"
                  )}
                  style={{
                    color: isActive("/app/settings") ? undefined : 'var(--sidebar-text-color, #ffffff)',
                    backgroundColor: isActive("/app/settings") ? undefined : 'transparent',
                  }}
                  title={collapsed ? "Settings" : undefined}
                >
                  <span className={cn("flex-shrink-0", collapsed ? "" : "mr-3")}><Settings className="h-5 w-5" /></span>
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-left">Settings</span>
                      <ChevronDown className={cn(
                        "h-4 w-4 transition-transform flex-shrink-0",
                        isSettingsOpen ? "rotate-180" : ""
                      )} />
                    </>
                  )}
                </button>
                {!collapsed && isSettingsOpen && (
                  <div className="ml-1 space-y-1 border-l border-border ml-5 pl-3">
                    {settingsSubmenuItems.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        data-nav-link
                        className={cn(
                          "flex items-center rounded-md text-xs font-medium transition-all duration-200 ease-in-out px-3 py-1.5 relative",
                          isActive(item.href)
                            ? "bg-primary/15 text-primary border-l-2 border-primary"
                            : "hover:bg-gray-600 hover:text-white hover:border-l-2 hover:border-primary hover:translate-x-0.5 hover:shadow-md"
                        )}
                        style={{
                          color: isActive(item.href) ? undefined : 'var(--sidebar-text-color, #ffffff)',
                          backgroundColor: isActive(item.href) ? undefined : 'transparent',
                        }}

                      >
                        <span className="mr-2 flex-shrink-0 [&>svg]:h-3 [&>svg]:w-3">
                          {item.icon}
                        </span>
                        <span className="truncate">{item.title.trim()}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </ConditionalRender>
          </nav>
        </div>
        
        {/* Collapse/Expand button */}
        <button
          onClick={onToggleCollapse}
          className="absolute bottom-3 right-3 p-1.5 rounded-full transition-colors hover:bg-primary hover:text-white"
          style={{
            backgroundColor: 'var(--sidebar-hover-bg-color, #374151)',
            color: 'var(--sidebar-text-color, #ffffff)',
          }}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
      </div>

    </aside>
  );
}